import type {
	InputGroup,
	Option,
	StartWithWithoutGeneric,
} from "@typedefs/modulesInterfaces";

// Re-export shared utilities from the canonical location so existing page
// imports continue to work without changes.
// See docs/adr/0001-feature-vs-page-layering.md
export {
	checkErrorsTouched,
	filterObjectProps,
	convertIncomingPercentage,
	convertOutgoingPercentage,
	convertEmptyStringsToNull,
	getThreadIds,
} from "@shared/modulePayload";
export type { Errors, Touched } from "@shared/modulePayload";

export const fertilizers = {
	limestone: { label: "Limestone", unit: "t/yr" },
	dolomite: { label: "Dolomite", unit: "t/yr" },
	urea: { label: "Urea", unit: "t/yr" },
	syntheticFertilizer: { label: "Synthetic N-Fertilizer", unit: "tN/yr" },
	phosphorus: { label: "Phosphorus", unit: "tP2O5/yr" },
	potassium: { label: "Potassium", unit: "tK2O/yr" },
	inIrrigatedRice: {
		label: "N-Fertilizer in Continuously irrigated Rice",
		unit: "tN/yr",
	},
	inWetDryRice: {
		label: "N-Fertilizer in Wet-Dry Irrigated Rice",
		unit: "tN/yr",
	},
	sewage: { label: "Sewage", unit: "tN/yr" },
	//compost: { label: "Compost", unit: "tN/yr"" },
	wasteAngGuano: { label: "Waste and Guano", unit: "tN/yr" },
	//userDefined: { label: "User Defined", unit: "t/yr" },
};

export const pesticides = {
	fungicides: { label: "Fungicides", unit: "tAI/yr" },
	herbicides: { label: "Herbicides", unit: "tAI/yr" },
	insecticides: { label: "Insecticides", unit: "tAI/yr" },
};

export const animalFeed = {
	livestockFeed: { label: "Livestock Feed", unit: "tFeed/yr" },
	fishFeed: { label: "Fish Feed", unit: "tFeed/yr" },
	/* userDefined: {label: "User Defined", unit: "t/yr"},*/
};

const initValues = {
	type: "",
	units: { start: 0, with: 0, without: 0 },
};



export const submodules = [
	{
		name: "Fertilizers",
		inputName: "module.fertilizers",
		initialValues: initValues,
		map: fertilizers,
	},
	{
		name: "Pesticides",
		inputName: "module.pesticides",
		initialValues: initValues,
		map: pesticides,
	},
	{
		name: "Animal Feed",
		inputName: "module.animalFeed",
		initialValues: initValues,
		map: animalFeed,
	},
];

export interface InputModuleValues {
	type: string;
	units: {
		start: number;
		with: number;
		without: number;
	};
}



export const replaceSWWOptions = (
	inputGroup: StartWithWithoutGeneric,
	optionList: Option[],
): StartWithWithoutGeneric => {
	return {
		...inputGroup,
		startProps: { dropdownOptions: optionList },
		withProps: { dropdownOptions: optionList },
		withoutProps: { dropdownOptions: optionList },
	};
};

export const deepEqual = (obj1: any, obj2: any): boolean => {
	// Check if both objects are null or undefined
	if (
		obj1 === null ||
		obj1 === undefined ||
		obj2 === null ||
		obj2 === undefined
	) {
		return obj1 === obj2;
	}

	// Check if both objects are of the same type
	if (typeof obj1 !== "object" || typeof obj2 !== "object") {
		return obj1 === obj2;
	}

	const keys1 = Object.keys(obj1);
	const keys2 = Object.keys(obj2);

	if (keys1.length !== keys2.length) {
		return false;
	}

	for (const key of keys1) {
		if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
			return false;
		}
	}

	return true;
};

export const instanceOfSWW = (
	obj: InputGroup,
): obj is StartWithWithoutGeneric => {
	return "startProps" in obj || "withProps" in obj || "withoutProps" in obj;
};

export const canBeNumber = (value: string | undefined) => {
	return !!value && !Number.isNaN(value) && value.trim() !== "";
}

export function isNumeric(value: string | undefined): boolean {
	return !!value && !Number.isNaN(Number(value)) &&
		!Number.isNaN(Number.parseFloat(value)) &&
		/^-?\d+(\.\d+)?$/.test(value) &&
		value.trim() !== "";
}

interface JustId { id: number | null }

export function mergeLocalAndServerChanges<T extends JustId>({ currentChangesList, incomingList }: { currentChangesList: T[], incomingList: T[] }) {
	const incomingIds = new Set<number>();
	const resultList: T[] = [];

	// First, build a set of all valid IDs from incoming list
	for (const item of incomingList) {
		if (item.id !== null) {
			incomingIds.add(item.id);
		}
	}

	// Only keep items from currentChangesList if they have null ID or their ID exists in incoming list
	for (const item of currentChangesList) {
		if (item.id === null || incomingIds.has(item.id)) {
			resultList.push(item);
		}
	}

	// Add new items from incomingList that aren't already in currentChangesList
	const currentIds = new Set(resultList.map(item => item.id).filter((id): id is number => id !== null));
	for (const item of incomingList) {
		if (item.id === null || !currentIds.has(item.id)) {
			resultList.push(item);
		}
	}

	return resultList;
}

export const preventFormSubmit = (event: React.KeyboardEvent) => {
	if (event.key === 'Enter' && event.currentTarget.tagName !== 'TEXTAREA') {
		event.preventDefault();
	}
};

export const roundToDecimals = (value: number, decimals: 1 | 2 | 3 | 4): number => {
	const factor = Math.pow(10, decimals);
	return Math.round(value * factor) / factor;
}

export const checkNullorUndefined = (value: number | null | undefined): boolean => {
	return value === null || value === undefined;
}

export const t2DivVariants = {
	open: {
		width: 550,
		transition: {
			type: "spring",
			stiffness: 20,
			restDelta: 2,
		},
		closed: {
			width: 0,
			transition: {
				delay: 0.5,
				type: "spring",
				stiffness: 400,
				damping: 40,
			},
		},
	},
};